/*! *********************************************************************
 *
 * Copyright 2022 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *************************************************************************
 */

import { Fragment, useState } from 'react';
import {
  Labeled,
  FileInput,
  FileField,
  BooleanInput,
  SaveButton,
  required,
  SelectInput,
  useInput,
  useRefresh,
} from 'react-admin';
import { Form } from 'react-final-form';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Typography,
} from '@material-ui/core';
import _get from 'lodash/get';
import _map from 'lodash/map';

import AddIcon from '@material-ui/icons/Add';
import FormHelperText from '@material-ui/core/FormHelperText';

import {
  getDownload,
  DOWNLOAD_FORMATS_LABEL,
  MAIN_DOWNLOAD_FORMAT,
  FORMATS_FILE_TYPE,
} from '../models/asset';
import {
  UPDATE_ASSET_FILE,
  ADD_DOWNLOAD_ATTACHMENT,
  UPDATE_DOWNLOAD_ATTACHMENT_FILE,
} from '../graphql/attachments';
import auth from '../providers/auth-service';
import uploadFile from '../services/UploadFile';
import { mutateGraphQL } from '../helpers/dataHelpers';

const validateType = [required()];
const validateFile = [required()];

export default function AttachmentDialog(props) {
  const {
    input: { onChange },
    meta: { error },
  } = useInput(props);
  const refresh = useRefresh();
  const [open, setOpen] = useState(false);
  const [updating, setUpdating] = useState(false);

  const openDialog = () => {
    setOpen(true);
  };

  const closeDialog = () => {
    setOpen(false);
  };

  const handleClick = () => {
    document
      .querySelector('#add-attachment')
      .dispatchEvent(new Event('submit', { cancelable: true }));
  };

  const handleSubmit = async (attachment) => {
    const file = _get(attachment, 'file.rawFile');
    const type = _get(attachment, 'type');
    const revisions = props.record[type];
    const assetId = _get(props, 'record.id');
    const assetType = _get(props, 'record.type');
    const attachmentId = _get(revisions, '[0].id');
    const isNewVersion = _get(attachment, 'isNewVersion');

    setUpdating(true);

    const accessToken = await auth.getAccessToken();
    const uploadedFile = await uploadFile(file, accessToken);

    let updatedAsset;
    if (attachmentId) {
      if (MAIN_DOWNLOAD_FORMAT[assetType] === type) {
        updatedAsset = await mutateGraphQL(UPDATE_ASSET_FILE, {
          isNewVersion: isNewVersion,
          fileId: _get(uploadedFile, 'body.id'),
          id: assetId,
        });
      } else {
        updatedAsset = await mutateGraphQL(UPDATE_DOWNLOAD_ATTACHMENT_FILE, {
          isNewVersion: isNewVersion,
          fileId: _get(uploadedFile, 'body.id'),
          attachmentId: attachmentId,
          assetId: assetId,
        });
      }
    } else {
      updatedAsset = await mutateGraphQL(ADD_DOWNLOAD_ATTACHMENT, {
        fileId: _get(uploadedFile, 'body.id'),
        assetId: assetId,
        extraTags: [type],
        label: DOWNLOAD_FORMATS_LABEL[type],
      });
    }

    const attachments = getDownload(
      _get(updatedAsset, 'addDownloadAttachment.attachments')
    );
    onChange(attachments);
    refresh();
    closeDialog();
  };

  return (
    <Fragment>
      <Labeled label="Add or update an asset attachment. It will be saved automatically.">
        <Button
          size="small"
          variant="contained"
          color="primary"
          onClick={openDialog}
        >
          <AddIcon />
          Add attachment
        </Button>
      </Labeled>
      {error && <FormHelperText error={true}>{error}</FormHelperText>}

      <Dialog
        size="large"
        open={open}
        onClose={closeDialog}
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle id="form-dialog-title">Add attachment</DialogTitle>
        <DialogContent>
          <Form onSubmit={handleSubmit}>
            {({ handleSubmit, values }) => (
              <form
                id="add-attachment"
                toolbar={null}
                record={values}
                onSubmit={handleSubmit}
              >
                <SelectInput
                  source="type"
                  choices={_map(DOWNLOAD_FORMATS_LABEL, (label, format) => ({
                    id: format,
                    name: label,
                  }))}
                  validate={validateType}
                />
                <BooleanInput label="Is New Version" source="isNewVersion" />
                <FileInput
                  label="Attachment File"
                  source="file"
                  placeholder={<p>Drop your attachment file here</p>}
                  validate={validateFile}
                  accept={FORMATS_FILE_TYPE[values.type]}
                >
                  <FileField source="url" title="title" target="_blank" />
                </FileInput>
              </form>
            )}
          </Form>
        </DialogContent>
        <DialogActions>
          {updating ? (
            <Typography align="center" style={{ width: '100%' }}>
              Update in progress... <CircularProgress size={20} />
            </Typography>
          ) : (
            <>
              <Button size="small" onClick={closeDialog}>
                Cancel
              </Button>
              <SaveButton
                size="small"
                color="primary"
                handleSubmitWithRedirect={handleClick}
                label="Add attachment"
              >
                Submit
              </SaveButton>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Fragment>
  );
}
